import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { HIGHLIGHT_COLORS } from '@jobos/shared/types/job';
import type { JobDetail } from '@jobos/shared/types/job';
import { useArchiveJob, useDuplicateJob, useUpdateJob } from '@/hooks/useJobs';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { HIGHLIGHT_COLOR_MAP } from '../utils/highlightColors';

// Component for the actions dropdown in each row
export function JobActionsDropdown({ job }: { job: JobDetail }) {
  const navigate = useNavigate();
  const { mutateAsync: archiveJob } = useArchiveJob(job.id);
  const { mutateAsync: duplicateJob, isPending: isDuplicating } = useDuplicateJob();
  const { mutate: updateJob } = useUpdateJob(job.id, { showToast: false });

  const handleDuplicate = async () => {
    const newJob = await duplicateJob(job);
    navigate(`/jobs/${newJob.id}`);
  };

  const [open, setOpen] = useState(false);

  return (
    <>
      {isDuplicating && <LoadingOverlay message="Duplicating job…" />}
      <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Job actions">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <DropdownMenuItem
          onClick={() => {
            if (!isDuplicating) handleDuplicate();
          }}
          data-disabled={isDuplicating ? '' : undefined}
        >
          Duplicate Job
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => archiveJob(!job.isArchived)}>
          {job.isArchived ? 'Unarchive Job' : 'Archive Job'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-gray-500 font-medium">Highlight</div>
        <div className="flex gap-1.5 px-2 py-1.5">
          {HIGHLIGHT_COLORS.map((color) => {
            const config = HIGHLIGHT_COLOR_MAP[color];
            const isActive = job.highlightColor === color;
            return (
              <button
                key={color}
                onClick={() => {
                  updateJob({ highlightColor: color });
                  setOpen(false);
                }}
                className="w-[22px] h-[22px] rounded-full border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: config.bg,
                  borderColor: isActive ? config.border : 'transparent',
                }}
                title={color}
              />
            );
          })}
        </div>
        {job.highlightColor && (
          <DropdownMenuItem
            onClick={() => updateJob({ highlightColor: null })}
            className="text-xs text-gray-400"
          >
            Remove Highlight
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
